import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanBlogAuthorPostDetail,
  getYowanAdminMutationErrorMessage,
  updateYowanBlogAuthorPost,
} from '../app/yowanContentClient';
import {
  AdminContentEditorFields,
  AdminPageHeader,
  useBlogAuthorContentMediaOptions,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  BlogAuthorGuardNotice,
  BlogAuthorShell,
  useBlogAuthorGuard,
} from './BlogAuthorDashboardPage';
const { yowanPageContent } = require('./pageContent');

const BlogAuthorPostEditPage = ({ guardOptions }) => {
  const content = yowanPageContent['/blog-author/posts/:postId/edit'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const { postId } = useParams();
  const guard = useBlogAuthorGuard(guardOptions);
  const canPublish = Boolean(guard.currentUser?.canPublishOwnBlogPosts || guard.currentUser?.canAccessAdmin);
  const [currentRecord, setCurrentRecord] = useState(null);
  const {
    mediaAssetOptions,
    mediaAssetLoading,
    mediaAssetError,
  } = useBlogAuthorContentMediaOptions(currentRecord?.primary_image);
  const [isLoading, setIsLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord || {
        title: '',
        slug: '',
        isPublished: false,
        publishedAt: '',
        excerpt: '',
        body: '',
      },
  });

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setSubmitMessage('');

    fetchYowanBlogAuthorPostDetail(postId)
      .then((record) => {
        if (!isMounted) {
          return;
        }

        setCurrentRecord(record);
        reset(record);
      })
      .catch(() => {
        if (isMounted) {
          setCurrentRecord(null);
          setSubmitMessage(content?.form.loadErrorMessage || '投稿を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.form.loadErrorMessage, guard.status, postId, reset]);

  if (guard.status !== 'ready') {
    return <BlogAuthorGuardNotice notice={guard.notice} />;
  }

  if (!content) {
    return null;
  }

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await updateYowanBlogAuthorPost(postId, formValues, { canPublish });
      navigate(appendYowanPreviewQueryIfNeeded('/blog-author/posts', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !currentRecord) {
    return (
      <BlogAuthorShell activeNavHref="/blog-author/posts" routeKey="/blog-author/posts/:postId/edit">
        <AdminPageHeader
          title={content.header.title}
          paragraphs={content.header.paragraphs}
          actions={[
            <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/blog-author/posts', preservedDomainSearch)}>
              一覧へ戻る
            </Link>,
          ]}
        />
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner /> : <p className="yowan-admin-form__message">{submitMessage || content.form.loadErrorMessage}</p>}
        </div>
      </BlogAuthorShell>
    );
  }

  return (
    <BlogAuthorShell activeNavHref="/blog-author/posts" routeKey="/blog-author/posts/:postId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/blog-author/posts', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
          currentRecord.publicHref ? (
            <Link key="public-preview" to={appendYowanPreviewQueryIfNeeded(currentRecord.publicHref, preservedDomainSearch)}>
              公開面
            </Link>
          ) : null,
        ].filter(Boolean)}
      />

      <form className="yowan-admin-form yowan-blog-author-post-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        <AdminContentEditorFields
          content={content}
          register={register}
          errors={errors}
          requireTitleAndSlug={false}
          slugReadOnly
          showPublishControls={canPublish}
          showNotifyOnPublishField={false}
          showMediaFields
          mediaAssetOptions={mediaAssetOptions}
          mediaAssetLoading={mediaAssetLoading}
          mediaAssetError={mediaAssetError}
          currentImage={currentRecord.primary_image}
        />
        {!canPublish ? (
          <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
            {content.form.noPublishPermissionHelpText}
          </p>
        ) : null}
        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {content.form.submitLabel}
          </button>
        </div>
        {isSubmitting ? (
          <p className="yowan-admin-form__message yowan-admin-form__message--pending">
            <LoadingSpinner inline size="sm" />
            <span>{content.form.pendingMessage}</span>
          </p>
        ) : submitMessage ? (
          <p className="yowan-admin-form__message">{submitMessage}</p>
        ) : null}
      </form>
    </BlogAuthorShell>
  );
};

export default BlogAuthorPostEditPage;
